// Call controls (SPEC §15.1): Dial (Space/D), Hang up (H), Mute (M), Keypad (K), Drop VM (V), live VM (L), Book (B), Skip (S),
// Call back (C, owner request PJ 2026-10-06: a call already saved, while the lead is on screen and within 15 minutes of it).
// Every button reflects the call state machine, so a control is only enabled when that action is legal right now.
// PJ 2026-10-20: a lead called recently (only soft attempt rules failing) keeps Dial enabled as "Call again"; pressing it
// asks "Call this number again?" (RedialConfirm) before anything dials.
// PJ 2026-10-20: on a connected call the recording rule skipped for all-party consent (the admin setting on), "Record
// (they said yes)" opens a confirm that says to ask first (ConsentRecordConfirm); once on, "Stop recording" for when they
// take the yes back. Its problems are one line under the bar; the call goes on.
// PJ 2026-10-23: the number line says which number Dial calls ("Owner's line (likely cell)") and, during and after a
// call, which number it went to ("Owner's line" / "Store line"); "Call the store (A)" sits beside Dial when the lead has
// both (S stays Skip).
import type { DialerState } from './store';
import {
  askConsentRecording, callBackButton, callBackNotice, callBackNow, callBackTitle, callOtherLine, clearCallError, confirmableGate, dial, dismissConsentRecError,
  dropBlockReason, dropVoicemail, hangup, skip, stopConsentRecording, toggleMute,
} from './store';
import { CONSENT_RECORDING_ON, consentRecView } from './consentRecord';
import type { PhoneState } from '../phone/manager';
import { phone } from '../phone/manager';
import { useNow } from '../components/ui-extra';
import { fmtPhone } from './labels';
import { fmtDuration } from './time';
import { callBarNumber, storeButton } from './numbers';


function PhaseLabel(props: { s: DialerState; now: number }) {
  const c = props.s.call;
  switch (c.phase) {
    case 'idle': {
      const confirm = confirmableGate(props.s.item?.dialGate);
      // PJ 2026-10-28: a meeting booked says so, not "called recently".
      return confirm
        ? <span class="vd-phase vd-phase-owed">{confirm.meeting ? 'Meeting booked.' : 'Called recently.'} <kbd>Space</kbd> asks first, then dials.</span>
        : <span class="vd-phase">Ready. Nothing dials until you press <kbd>Space</kbd>.</span>;
    }
    case 'preparing': return <span class="vd-phase vd-phase-busy">Checking the calling rules…</span>;
    case 'blocked': return <span class="vd-phase vd-phase-bad">Can't call this lead right now</span>;
    case 'connecting': return <span class="vd-phase vd-phase-busy">Connecting…</span>;
    case 'ringing': return <span class="vd-phase vd-phase-busy">Ringing {c.startedAt ? fmtDuration((props.now - c.startedAt) / 1000) : ''}</span>;
    case 'in_call': return <span class="vd-phase vd-phase-live">On the call {c.answeredAt ? fmtDuration((props.now - c.answeredAt) / 1000) : ''}{c.dropping ? ' · dropping voicemail…' : c.serverDisposition === 'vm_dropped' ? ' · voicemail dropped' : ''}</span>;
    case 'ended': return <span class="vd-phase">{c.dropping ? 'Call ended. Confirming the voicemail drop…' : 'Call ended. Checking…'}</span>;
    case 'needs_disposition': return <span class="vd-phase vd-phase-owed">Log the outcome below, then Enter</span>;
  }
}

export function CallBar(props: {
  s: DialerState; phoneState: PhoneState; userId: number; keypadOpen: boolean; onKeypad: () => void;
}) {
  const { s } = props;
  const now = useNow(1000);
  const item = s.item;
  const c = s.call;
  const live = c.phase === 'connecting' || c.phase === 'ringing' || c.phase === 'in_call';
  const idle = c.phase === 'idle' || c.phase === 'blocked';
  const cantCall = idle ? phone.whyCantCall() : null;
  const dropWhy = dropBlockReason(s, props.userId);
  const muted = props.phoneState.active?.muted ?? false;
  const blur = (e: Event) => (e.currentTarget as HTMLElement).blur();
  // "Call back now": its button lives here on the lead card (idle / blocked) and in the wrap-up dock (owed). A refusal by
  // the calling rules is the disabled button's hover text; it is spelled out under the call bar only once C was pressed
  // (or the server refused at press time), so the bar doesn't fill with lines nobody asked for.
  const cb = callBackButton(s, now);
  const cbRefused = callBackNotice(s, now);
  const confirm = confirmableGate(item?.dialGate);
  const again = !!confirm;
  // PJ 2026-10-28: a lead with a meeting booked reads "Call anyway" and asks "Meeting booked. Call anyway?" first.
  const meeting = confirm?.meeting === true;
  const rec = consentRecView(c, s.consentRec);
  const num = item ? callBarNumber(item, c.to) : null;
  // PJ 2026-10-23: "Call the store (A)": the lead's store line when Dial calls the owner's.
  const store = storeButton(item);
  const storeHard = store && !store.gate.ok && !store.gate.confirm ? store.gate.reasons[0]?.message ?? "The store line can't be called right now." : null;
  const recError = s.consentRec && s.consentRec.callId === c.callId ? s.consentRec.error : null;

  return (
    <div class={`vd-callbar vd-callbar-${c.phase}`}>
      <div class="vd-callbar-lead">
        {item ? (
          <>
            <div class="vd-callbar-name" title={item.business.name}>{item.business.name}</div>
            <div class="vd-callbar-phone mono">
              {num?.label ? <span class="vd-number-role">{num.label} </span> : null}
              {fmtPhone(num?.e164)}{num?.ext ? ` ext. ${num.ext}` : ''}
              {c.callerId ? <span class="muted"> · from {fmtPhone(c.callerId)}</span> : null}
            </div>
          </>
        ) : <div class="muted">No lead on screen</div>}
      </div>

      <div class="vd-callbar-state">
        <PhaseLabel s={s} now={now} />
        {props.phoneState.warnings.length && live ? <span class="chip chip-warn">{props.phoneState.warnings[0]}</span> : null}
        {props.phoneState.active?.reconnecting ? <span class="chip chip-warn">Reconnecting…</span> : null}
      </div>

      <div class="vd-callbar-controls">
        {idle ? (
          <>
            <button class={`btn vd-dial ${c.phase === 'blocked' ? '' : 'btn-primary'}`} disabled={!item || s.lead !== 'ready' || !!cantCall}
              title={cantCall ?? (meeting ? 'Meeting booked: asks "Meeting booked. Call anyway?" first, then dials'
                : again ? 'Called recently: asks "Call this number again?" first, then dials' : 'Dial this lead')}
              onClick={(e) => { blur(e); void dial(); }}>
              {meeting ? 'Call anyway' : again ? 'Call again' : 'Dial'} <kbd>Space</kbd>
            </button>
            {store ? (
              <button class="btn" disabled={!item || s.lead !== 'ready' || !!cantCall || !!storeHard}
                title={cantCall ?? storeHard ?? (store.gate.confirm?.meeting ? `Meeting booked: asks "Meeting booked. Call anyway?" first, then dials the store line ${fmtPhone(store.e164)}`
                  : store.gate.confirm ? `Called recently: asks "Call this number again?" first, then dials the store line ${fmtPhone(store.e164)}` : `Dials the store line ${fmtPhone(store.e164)} instead of the owner's`)}
                onClick={(e) => { blur(e); void callOtherLine(); }}>Call the store <kbd>A</kbd></button>
            ) : null}
            {cb ? (
              <button class="btn" disabled={!!cb.why || !!cantCall} title={cb.why ?? cantCall ?? callBackTitle(s)}
                onClick={(e) => { blur(e); void callBackNow(); }}>Call back <kbd>C</kbd></button>
            ) : null}
            <button class="btn" disabled={!item || s.lead === 'loading'} onClick={(e) => { blur(e); void skip(); }}>Skip <kbd>S</kbd></button>
          </>
        ) : null}
        {c.phase === 'preparing' ? <button class="btn" onClick={(e) => { blur(e); hangup(); }}>Cancel <kbd>H</kbd></button> : null}
        {live ? (
          <>
            <button class={`btn ${muted ? 'btn-ink' : ''}`} onClick={(e) => { blur(e); toggleMute(); }} aria-pressed={muted}>{muted ? 'Unmute' : 'Mute'} <kbd>M</kbd></button>
            <button class={`btn ${props.keypadOpen ? 'btn-ink' : ''}`} onClick={(e) => { blur(e); props.onKeypad(); }}>Keypad <kbd>K</kbd></button>
            <button class="btn" disabled={!!dropWhy || c.dropping} title={dropWhy ?? 'Drop your approved recording and move on'}
              onClick={(e) => { blur(e); void dropVoicemail(props.userId); }}>{c.dropping ? 'Dropping…' : <>Drop VM <kbd>V</kbd></>}</button>
            {rec.kind === 'offer' ? (
              <button class="btn" disabled={rec.busy} title="Their state needs everyone's consent to record. Ask first; start only after they say yes."
                onClick={(e) => { blur(e); askConsentRecording(); }}>{rec.busy ? 'Starting…' : 'Record (they said yes)'}</button>
            ) : null}
            {rec.kind === 'on' ? (
              <>
                <span class="chip chip-ok" title={`Say it out loud: "${CONSENT_RECORDING_ON}"`}>Recording (they said yes)</span>
                <button class="btn" disabled={rec.busy} title="They took the yes back: stop it now. What was recorded with their yes is kept."
                  onClick={(e) => { blur(e); void stopConsentRecording(); }}>{rec.busy ? 'Stopping…' : 'Stop recording'}</button>
              </>
            ) : null}
            {rec.kind === 'stopped' ? <span class="chip">Recording stopped</span> : null}
            <button class="btn btn-danger vd-hangup" onClick={(e) => { blur(e); hangup(); }}>Hang up <kbd>H</kbd></button>
          </>
        ) : null}
      </div>

      {c.error ? (
        <div class="vd-callbar-error">
          <span>{c.error}</span>
          <button class="btn btn-sm" onClick={clearCallError} aria-label="Dismiss">OK</button>
        </div>
      ) : null}
      {idle && cantCall && item ? <div class="vd-callbar-error"><span>{cantCall}</span></div> : null}
      {recError ? (
        <div class="vd-callbar-error">
          <span>{recError}</span>
          <button class="btn btn-sm" onClick={dismissConsentRecError} aria-label="Dismiss">OK</button>
        </div>
      ) : null}
      {cbRefused && c.phase !== 'blocked' ? <div class="vd-callbar-error"><span>Can't call back now: {cbRefused}</span></div> : null}
    </div>
  );
}
